import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Sun, Thermometer, AlertTriangle, Coffee, ChevronLeft, ChevronRight, Edit3, Save } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';

const ABSENCE_LABELS = {
  vacances: 'Vacances', recup: 'Récup', recuperation: 'Récup',
  maladie: 'Maladie', accident: 'Accident', non_paye: 'Non payé',
  conge_non_paye: 'Non payé', conge: 'Congé',
};
const ABSENCE_COLORS = {
  vacances: 'bg-blue-100 text-blue-700',
  recup: 'bg-violet-100 text-violet-700', recuperation: 'bg-violet-100 text-violet-700',
  maladie: 'bg-red-100 text-red-700',
  accident: 'bg-orange-100 text-orange-700',
  non_paye: 'bg-slate-100 text-slate-700', conge_non_paye: 'bg-slate-100 text-slate-700',
  conge: 'bg-cyan-100 text-cyan-700',
};

function CounterCard({ icon: Icon, label, value, sub, color }) {
  return (
    <Card className="p-4 text-center space-y-1" data-testid={`counter-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <Icon className={`w-5 h-5 mx-auto ${color}`} />
      <p className="text-2xl font-bold text-slate-800">{value}</p>
      <p className="text-xs font-medium text-slate-500">{label}</p>
      {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
    </Card>
  );
}

export default function EmployeeHRPage({ auth, onLogout }) {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
  });
  const [loading, setLoading] = useState(true);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualForm, setManualForm] = useState({ date: '', hours: '', clock_in: '', clock_out: '' });
  const [year, setYear] = useState(new Date().getFullYear());

  const loadData = useCallback(async () => {
    try {
      const [empRes, sumRes, monthRes] = await Promise.all([
        axios.get(`${API}/employees/${employeeId}`).catch(() => null),
        axios.get(`${API}/employees/${employeeId}/hr-summary?year=${year}`),
        axios.get(`${API}/employees/${employeeId}/hr-monthly?month=${currentMonth}`),
      ]);
      if (empRes) setEmployee(empRes.data);
      setSummary(sumRes.data);
      setMonthly(monthRes.data);
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  }, [employeeId, year, currentMonth]);

  useEffect(() => { loadData(); }, [loadData]);

  const prevMonth = () => {
    const [y, m] = currentMonth.split('-').map(Number);
    const d = new Date(y, m - 2, 1);
    setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };
  const nextMonth = () => {
    const [y, m] = currentMonth.split('-').map(Number);
    const d = new Date(y, m, 1);
    setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const submitManualEntry = async () => {
    if (!manualForm.date || !manualForm.hours) { toast.error('Date et heures requises'); return; }
    try {
      await axios.post(`${API}/employees/${employeeId}/manual-entry`, manualForm);
      toast.success('Entrée sauvegardée');
      setShowManualEntry(false);
      loadData();
    } catch { toast.error('Erreur'); }
  };

  const empName = employee ? `${employee.first_name || ''} ${employee.last_name || ''}`.trim() : '...';
  const monthLabel = currentMonth ? new Date(currentMonth + '-01').toLocaleDateString('fr-CH', { month: 'long', year: 'numeric' }) : '';

  if (loading) return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="max-w-5xl mx-auto space-y-5" data-testid="employee-hr-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/employees')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-800">{empName}</h1>
              <p className="text-sm text-slate-500">Heures & Absences — {year}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setYear(y => y - 1)} data-testid="prev-year">{year - 1}</Button>
            <span className="px-3 py-1.5 bg-slate-100 rounded text-sm font-bold">{year}</span>
            <Button variant="outline" size="sm" onClick={() => setYear(y => y + 1)} data-testid="next-year">{year + 1}</Button>
          </div>
        </div>

        {/* Counters */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            <CounterCard icon={Sun} label="Vacances" value={`${summary.vacation.taken}/${summary.vacation.entitled}`} sub={`${summary.vacation.remaining}j restants`} color="text-blue-500" />
            <CounterCard icon={Coffee} label="Récup" value={`${summary.absences.recup}j`} color="text-violet-500" />
            <CounterCard icon={Thermometer} label="Maladie" value={`${summary.absences.maladie}j`} color="text-red-500" />
            <CounterCard icon={AlertTriangle} label="Accident" value={`${summary.absences.accident}j`} color="text-orange-500" />
            <CounterCard icon={Clock} label="H. semaine" value={`${summary.hours.week}h`} color="text-emerald-500" />
            <CounterCard icon={Clock} label="H. mois" value={`${summary.hours.month}h`} color="text-emerald-500" />
            <CounterCard icon={Clock} label="H. année" value={`${summary.hours.year}h`} color="text-emerald-500" />
          </div>
        )}

        {/* Monthly breakdown */}
        <Card className="p-4 space-y-3" data-testid="monthly-breakdown">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <h2 className="text-sm font-bold text-slate-700 capitalize">{monthLabel}</h2>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowManualEntry(true)} data-testid="add-manual-entry">
                <Edit3 className="w-3.5 h-3.5 mr-1" /> Saisie manuelle
              </Button>
              <Button variant="ghost" size="sm" onClick={prevMonth}><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" onClick={nextMonth}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>

          {monthly && (
            <>
              <div className="flex gap-4 text-xs text-slate-500 pb-2 border-b">
                <span>Travaillé : <strong className="text-slate-800">{monthly.total_worked}h</strong></span>
                <span>Objectif : <strong className="text-slate-800">{monthly.total_target}h</strong></span>
                <span className={monthly.overtime >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                  {monthly.overtime >= 0 ? '+' : ''}{monthly.overtime}h
                </span>
              </div>

              <div className="space-y-0.5">
                {monthly.days.map(day => (
                  <div
                    key={day.date}
                    className={`flex items-center gap-3 px-2 py-1.5 rounded text-xs ${
                      day.is_weekend ? 'bg-slate-50 text-slate-400' :
                      day.absence_type ? 'bg-amber-50' : ''
                    }`}
                    data-testid={`day-${day.date}`}
                  >
                    <span className="w-8 font-medium text-slate-500">{day.day_name}</span>
                    <span className="w-20 font-mono text-slate-600">{day.date.slice(5)}</span>
                    {day.absence_type ? (
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${ABSENCE_COLORS[day.absence_type] || 'bg-slate-100 text-slate-600'}`}>
                        {ABSENCE_LABELS[day.absence_type] || day.absence_type}
                      </span>
                    ) : day.is_weekend ? (
                      <span className="text-slate-300">—</span>
                    ) : (
                      <>
                        <div className="flex-1">
                          <div className="flex items-center gap-1">
                            {day.clock_in && <span className="text-slate-400">{day.clock_in}</span>}
                            {day.clock_in && day.clock_out && <span className="text-slate-300">→</span>}
                            {day.clock_out && <span className="text-slate-400">{day.clock_out}</span>}
                          </div>
                        </div>
                        <span className={`font-mono font-bold w-12 text-right ${day.hours_worked > 0 ? 'text-slate-800' : 'text-slate-300'}`}>
                          {day.hours_worked > 0 ? `${day.hours_worked}h` : '—'}
                        </span>
                        {day.overtime !== 0 && (
                          <span className={`font-mono text-[10px] w-12 text-right ${day.overtime > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                            {day.overtime > 0 ? '+' : ''}{day.overtime}h
                          </span>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>

        {/* Leaves detail */}
        {summary && summary.leaves_detail.length > 0 && (
          <Card className="p-4 space-y-3" data-testid="leaves-detail">
            <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
              <Sun className="w-4 h-4 text-blue-500" /> Absences {year}
            </h2>
            <div className="space-y-1">
              {summary.leaves_detail.map(l => (
                <div key={l.id} className="flex items-center justify-between text-xs bg-slate-50 rounded px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full font-bold ${ABSENCE_COLORS[l.type] || 'bg-slate-100 text-slate-600'}`}>
                      {ABSENCE_LABELS[l.type] || l.type}
                    </span>
                    <span className="text-slate-600">{l.start} → {l.end}</span>
                  </div>
                  <span className="font-bold text-slate-800">{l.days}j</span>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Manual entry dialog */}
      <Dialog open={showManualEntry} onOpenChange={setShowManualEntry}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Saisie manuelle</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Date *</label>
              <Input type="date" value={manualForm.date} onChange={e => setManualForm(f => ({ ...f, date: e.target.value }))} data-testid="manual-date" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Heures travaillées *</label>
              <Input type="number" step="0.25" value={manualForm.hours} onChange={e => setManualForm(f => ({ ...f, hours: e.target.value }))} placeholder="8.5" data-testid="manual-hours" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-slate-600">Entrée</label>
                <Input type="time" value={manualForm.clock_in} onChange={e => setManualForm(f => ({ ...f, clock_in: e.target.value }))} data-testid="manual-clockin" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Sortie</label>
                <Input type="time" value={manualForm.clock_out} onChange={e => setManualForm(f => ({ ...f, clock_out: e.target.value }))} data-testid="manual-clockout" />
              </div>
            </div>
            <Button onClick={submitManualEntry} className="w-full" data-testid="submit-manual-entry">
              <Save className="w-4 h-4 mr-2" /> Sauvegarder
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
